import { Box, Stack } from '@mui/material'
import { useLayoutEffect, useRef, useState } from 'react'
import { COLORS } from '../../styles/colors'

type FloatingFieldProps = {
	value: string
	onChange: (value: string) => void
	placeholder: string
	error?: boolean
	helperText?: string
	width?: string | number
	suffix?: string
	centerWhenEmpty?: boolean
}

export const isCompleteDryMatter = (value: string) =>
	/^(?:100(?:\.0{1,2})?|\d{1,2}(?:\.\d{1,2})?|\d{1,2})$/.test(value)

export const sanitizeDryMatterInput = (rawValue: string) => {
	const inputValue = rawValue.replace(',', '.').replace(/^0+(?=\d)/, '')

	if (
		inputValue === '' ||
		inputValue === '.' ||
		/^(?:100(?:\.0{0,2})?|\d{1,2}(?:\.\d{0,2})?)$/.test(inputValue)
	) {
		return inputValue
	}

	return null
}

export const getDuplicateIndexes = (error: unknown): number[] => {
	if (typeof error !== 'object' || error === null || !('response' in error)) {
		return []
	}

	const indexes = (
		error as {
			response?: { data?: { payload?: { indexes?: number[] } } }
		}
	).response?.data?.payload?.indexes

	return Array.isArray(indexes) ? indexes : []
}

export const FloatingField = ({
	value,
	onChange,
	placeholder,
	error = false,
	helperText = '',
	width = '100%',
	suffix,
	centerWhenEmpty = false,
}: FloatingFieldProps) => {
	const inputRef = useRef<HTMLInputElement>(null)
	const labelRef = useRef<HTMLSpanElement>(null)
	const [focused, setFocused] = useState(false)
	const [restX, setRestX] = useState(16)
	const floated = focused || value.length > 0
	const showSuffix = Boolean(suffix && value)
	const borderColor = error
		? COLORS.error
		: focused
			? COLORS.main
			: COLORS.inputBorder
	const labelColor = error ? COLORS.error : focused ? COLORS.main : '#A0A0A0'
	const valueColor = focused ? COLORS.main : COLORS.black
	const labelX = floated ? 12 : centerWhenEmpty ? restX : 16
	const labelY = floated ? -9 : 15
	const labelScale = floated ? 0.86 : 1

	useLayoutEffect(() => {
		if (!centerWhenEmpty || !labelRef.current?.parentElement) return
		const parentWidth = labelRef.current.parentElement.clientWidth
		const labelWidth = labelRef.current.scrollWidth
		setRestX((parentWidth - labelWidth) / 2)
	}, [centerWhenEmpty, placeholder])

	return (
		<Stack gap='4px' sx={{ width }}>
			<Box
				onClick={() => inputRef.current?.focus()}
				sx={{
					position: 'relative',
					height: '48px',
					borderRadius: '8px',
					border: `1px solid ${borderColor}`,
					backgroundColor: '#fff',
					cursor: 'text',
					boxSizing: 'border-box',
					...(!focused &&
						!error && {
							'&:hover': {
								borderColor: COLORS.hoverInputBorder,
							},
						}),
				}}
			>
				<Box
					component='span'
					ref={labelRef}
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						zIndex: 1,
						fontSize: '14px',
						lineHeight: '17px',
						color: floated ? labelColor : '#A0A0A0',
						backgroundColor: floated ? '#fff' : 'transparent',
						boxShadow: floated ? '-4px 0 0 #fff, 4px 0 0 #fff' : 'none',
						transformOrigin: 'top left',
						transform: `translate(${labelX}px, ${labelY}px) scale(${labelScale})`,
						transition:
							'transform 200ms cubic-bezier(0, 0, 0.2, 1), color 200ms cubic-bezier(0, 0, 0.2, 1)',
						pointerEvents: 'none',
						whiteSpace: 'nowrap',
					}}
				>
					{placeholder}
				</Box>
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: centerWhenEmpty ? 'center' : 'flex-start',
						px: '16px',
					}}
				>
					<Box
						component='input'
						ref={inputRef}
						value={value}
						autoComplete='off'
						onChange={event => onChange(event.target.value)}
						onFocus={() => setFocused(true)}
						onBlur={() => setFocused(false)}
						sx={{
							border: 'none',
							outline: 'none',
							background: 'transparent',
							padding: 0,
							margin: 0,
							fontFamily: 'inherit',
							fontSize: '14px',
							lineHeight: '17px',
							color: COLORS.black,
							width: showSuffix
								? `${Math.max(value.length, 1)}ch`
								: centerWhenEmpty
									? '1ch'
									: '100%',
							minWidth: 0,
							textAlign: 'left',
						}}
					/>
					{showSuffix && (
						<Box
							component='span'
							sx={{
								ml: '4px',
								fontSize: '14px',
								lineHeight: '17px',
								color: valueColor,
								pointerEvents: 'none',
							}}
						>
							{suffix}
						</Box>
					)}
				</Box>
			</Box>
			{helperText && (
				<Box sx={{ color: COLORS.error, fontSize: '12px', lineHeight: '16px' }}>
					{helperText}
				</Box>
			)}
		</Stack>
	)
}
